import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { FieldDataType, RelatedObjectFormType } from "@/types/dynamic-detail";
import { renderField } from "@/utils/field-renderer";
import * as LucideIcons from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { ObjectListTableResizable } from "./object-list-table-resizable-2";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Location } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { DynamicObjectForm } from "./dynamic-object-form";
import { bugsnagNotify } from "@/utils/bugsnag-notify";
import { Actions2 } from "./actions-2";
import type { DynamicAction } from "@/types/dynamic-detail";

export type RelatedListItem = {
    id: string;
    /** Row address when `href_template` names something other than the row pk. */
    href?: string;
    fields: FieldDataType[];
    /** Per-row write rights, served only when the related list declares a form. */
    can_update?: boolean;
    can_delete?: boolean;
    /** Raw values of the form fields for this row, used to prefill the edit dialog. */
    form_values?: Record<string, any>;
    /** Names of the row actions this record is eligible for, decided by the backend. */
    available_actions?: string[];
    /** Names of the parent's row actions this record is eligible for, decided by the backend. */
    available_parent_actions?: string[];
    /** Per parent row action, the dialog's prefill on this row (`row_values_handler`), by field name. */
    parent_action_values?: Record<string, Record<string, unknown>>;
};

interface RelatedListProps {
    objectId: string | number;
    relatedName: string;
    relatedCode: string;
    objectApiUrl: string;
    fields_to_display: FieldDataType[];
    icon?: string | null;
    color?: string | null;
    onRowNavigateTo?: (instance: any, currentLocation: Location, event?: MouseEvent<HTMLTableCellElement>) => string | Map<string, string> | null;
    clickable_field?: string;
    pageSize?: number;
    hide_if_empty?: boolean;
    objectName?: string;
    refreshKey?: number;
    /** Backend-declared write surface: absent, the list stays read-only. */
    form?: RelatedObjectFormType | null;
    /** Actions on the record the list hangs from, offered in the card header. */
    actions?: DynamicAction[];
    /** Actions on a row, offered in a menu at the end of that row. */
    rowActions?: DynamicAction[];
    /** Endpoint of the child object, i.e. where a row action is executed. */
    childApiUrl?: string;
    /** Actions of the record the list hangs from, offered on a row with the row's id in their `row_field`. */
    parentRowActions?: DynamicAction[];
    /** Links drawn to the right of Add. `{id}` is replaced with `objectId`. */
    headerLinks?: { label: string; href_template: string }[];
}

function getFieldTextValue(field: FieldDataType | undefined): string {
    if (!field) return "";
    const val = field.value;
    if (val == null) return "";
    if (typeof val === "boolean") return val ? "Oui" : "Non";
    return String(val);
}

function ExpandedRowDetail({ item, fields_to_display }: { item: RelatedListItem; fields_to_display: FieldDataType[] }) {
    return (
        <div className="border-t border-gray-100 bg-gray-50/70 px-4 py-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {item.fields.map((field, i) => {
                    const config = fields_to_display.find(f => f.field_name === field.field_name);
                    const label = config?.label || field.label || field.field_name;
                    const textValue = getFieldTextValue(field);
                    const isLargeContent = textValue.length > 80;

                    return (
                        <div key={`${field.field_name}-${i}`} className={cn("flex flex-col gap-0.5", isLargeContent && "sm:col-span-2")}>
                            <span className="text-xs font-medium text-gray-500">{label}</span>
                            <div className={cn("overflow-wrap-anywhere whitespace-pre-wrap break-words text-sm text-gray-900")} title={textValue}>
                                {renderField(field, { showLabel: false, layout: "flex", disableFieldActions: true, truncate: false }) || <span className="text-gray-400">-</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function RelatedList({
    objectId,
    relatedName,
    relatedCode,
    objectApiUrl,
    fields_to_display,
    icon,
    color,
    onRowNavigateTo,
    clickable_field,
    pageSize = 5,
    hide_if_empty = false,
    objectName,
    refreshKey = 0,
    form,
    actions = [],
    rowActions: configuredRowActions = [],
    childApiUrl,
    parentRowActions = [],
    headerLinks = []
}: RelatedListProps) {
    const [results, setResults] = useState<RelatedListItem[]>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [columns, setColumns] = useState<string[]>([]);
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
    const [localRefresh, setLocalRefresh] = useState(0);
    const [createOpen, setCreateOpen] = useState(false);
    const [editItem, setEditItem] = useState<RelatedListItem | null>(null);
    const [deleteItem, setDeleteItem] = useState<RelatedListItem | null>(null);
    const [deleting, setDeleting] = useState(false);

    const onMutated = useCallback(() => {
        setLocalRefresh(v => v + 1);
    }, []);

    const formEndpointUrl = form ? `/crm/objects/${form.child_object_url}/` : "";

    const handleDelete = useCallback(async () => {
        if (!form || !deleteItem) return;
        setDeleting(true);
        try {
            await DataService.client.url(`${formEndpointUrl}form-action/`).post({ action_type: "__delete__", id: deleteItem.id }).json();
            toast.success("Élément supprimé");
            setDeleteItem(null);
            onMutated();
        } catch (e: any) {
            toast.error("Erreur lors de la suppression");
            bugsnagNotify("RelatedList > handleDelete()", { relatedCode, id: deleteItem.id }, e);
        } finally {
            setDeleting(false);
        }
    }, [form, deleteItem, formEndpointUrl, onMutated, relatedCode]);

    const configuredRowActionsFor = (item: RelatedListItem) => {
        const available = item.available_actions ?? [];
        return configuredRowActions.filter(action => available.includes(action.action_name));
    };

    const hasRowActions = (configuredRowActions.length > 0 && Boolean(childApiUrl)) || parentRowActions.length > 0;

    /** The parent's row actions this row may run, their `row_field` filled with the row's id and hidden. */
    const parentRowActionsFor = (item: RelatedListItem) => {
        const available = item.available_parent_actions ?? [];
        return parentRowActions
            .filter(action => available.includes(action.action_name))
            .map(action => {
                const rowValues = item.parent_action_values?.[action.action_name] ?? {};
                return {
                    ...action,
                    fields: action.fields.map(field => ({
                        ...field,
                        value: field.field_name === action.row_field ? String(item.id) : (rowValues[field.field_name] ?? field.value),
                        hidden: field.field_name === action.row_field || field.hidden
                    }))
                };
            });
    };

    const formRowActions =
        form && (form.permissions.update || form.permissions.delete)
            ? (item: RelatedListItem) => (
                  <>
                      {form.permissions.update && item.can_update && (
                          <Button size="sm" variant="ghost" title="Modifier" onClick={() => setEditItem(item)}>
                              <LucideIcons.Pencil className="h-3.5 w-3.5" />
                          </Button>
                      )}
                      {form.permissions.delete && item.can_delete && (
                          <Button size="sm" variant="ghost" title="Supprimer" className="text-destructive hover:text-destructive" onClick={() => setDeleteItem(item)}>
                              <LucideIcons.Trash2 className="h-3.5 w-3.5" />
                          </Button>
                      )}
                  </>
              )
            : null;

    const fetchPage = useCallback(
        (targetPage: number, replace: boolean) => {
            setLoading(true);
            setError(null);
            DataService.client
                .url(`${objectApiUrl}/${objectId}/related-list/${relatedCode}/`)
                .query({ page: targetPage, page_size: pageSize })
                .get()
                .json<any>()
                .then(data => {
                    const nextResults = Array.isArray(data.results) ? data.results : [];
                    setResults(prev => (replace ? nextResults : [...prev, ...nextResults]));
                    setCount(data.count || 0);
                    setColumns(data.columns || []);
                    setLoading(false);
                })
                .catch(err => {
                    setError(err.message || "Erreur lors du chargement");
                    setLoading(false);
                    if (replace) setResults([]);
                });
        },
        [objectApiUrl, objectId, relatedCode, pageSize]
    );

    // Single reset fetch — do not pair with a page>1 effect: when `fetchPage` identity
    // changes while `page` is still > 1, both would race (replace page 1 then append old page).
    useEffect(() => {
        setPage(1);
        fetchPage(1, true);
    }, [objectId, relatedCode, objectApiUrl, pageSize, refreshKey, localRefresh, fetchPage]);

    const loadMore = useCallback(() => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchPage(nextPage, false);
    }, [page, fetchPage]);

    const dynamicColumns = columns.map((col, i) => {
        const field = fields_to_display.find(f => f.field_name === col);
        return {
            accessorKey: `${col}-${i}` as any,
            accessorFn: (item: RelatedListItem) => {
                const field_data = item.fields.find((f: FieldDataType) => f.field_name === col);
                if (!field_data) return null;
                const textValue = getFieldTextValue(field_data);
                const rendered = renderField(field_data, { showLabel: false, layout: "flex", disableFieldActions: true });

                if (textValue.length > 40) {
                    return (
                        <TooltipProvider delayDuration={300}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="block truncate">{rendered}</span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap border border-gray-200 bg-white text-sm text-gray-900 shadow-lg">
                                    <p className="max-h-40 overflow-y-auto">{textValue}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                }

                return rendered;
            },
            fieldName: col,
            name: field?.label || col.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
        };
    });

    if (formRowActions || hasRowActions) {
        dynamicColumns.push({
            accessorKey: "actions" as any,
            accessorFn: (item: RelatedListItem) => {
                const availableActions = configuredRowActionsFor(item);
                const availableParentActions = parentRowActionsFor(item);
                return (
                    <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                        {formRowActions?.(item)}
                        {availableParentActions.length > 0 && (
                            <Actions2
                                layout="menu"
                                actions={availableParentActions}
                                selectedItemIds={[String(objectId)]}
                                selectAll={false}
                                selectAllCount={1}
                                endpointUrl={`${objectApiUrl}/`}
                                action_scope="detail"
                                onActionSuccess={onMutated}
                            />
                        )}
                        {availableActions.length > 0 && (
                            <Actions2
                                layout="menu"
                                actions={availableActions}
                                selectedItemIds={[String(item.id)]}
                                selectAll={false}
                                selectAllCount={1}
                                endpointUrl={`${childApiUrl}/`}
                                action_scope="detail"
                                onActionSuccess={onMutated}
                            />
                        )}
                    </div>
                );
            },
            fieldName: "actions",
            name: ""
        });
    }

    const paginated_response = { count, results };
    const IconComponent = (LucideIcons as any)[icon || "Logs"];
    const IconColor = color || "#52525b";

    // An empty list that can be written to still has to be shown: it is the only place the
    // first record can be created from.
    if (hide_if_empty && results.length === 0 && !loading && !form?.permissions.create && actions.length === 0) {
        return null;
    }

    const handleRowClick = (item: RelatedListItem) => {
        if (!onRowNavigateTo) {
            setExpandedRowId(prev => (prev === item.id?.toString() ? null : item.id?.toString()));
        }
    };

    return (
        <div className="mt-2 flex w-full flex-col items-start rounded-md bg-white py-4 shadow-md">
            <div className="flex w-full flex-col">
                <div className="text-md ml-4 mr-4 flex flex-row items-center justify-between gap-2 font-bold">
                    <div className="flex flex-row items-center gap-2">
                        <div className={cn("flex h-6 w-6 items-center justify-center rounded-sm p-1")} style={{ backgroundColor: IconColor || "#000000" }}>
                            {IconComponent && <IconComponent className="text-white" />}
                        </div>
                        {relatedName} ({results.length}
                        {count > results.length ? "+" : ""})
                    </div>
                    <div className="flex items-center gap-2">
                        {actions.length > 0 && (
                            <Actions2
                                layout="add"
                                actions={actions}
                                selectedItemIds={[String(objectId)]}
                                selectAll={false}
                                selectAllCount={1}
                                endpointUrl={`${objectApiUrl}/`}
                                action_scope="detail"
                                onActionSuccess={onMutated}
                            />
                        )}
                        {form?.permissions.create && (
                            <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
                                <LucideIcons.Plus className="mr-1 h-4 w-4" />
                                {form.config.create_label}
                            </Button>
                        )}
                        {headerLinks.map(link => (
                            <Button key={link.href_template} size="sm" variant="outline" asChild>
                                <Link to={link.href_template.replace("{id}", String(objectId))}>{link.label}</Link>
                            </Button>
                        ))}
                    </div>
                </div>

                {error && <div className="py-4 text-center text-red-500">{error}</div>}

                {results.length > 0 && (
                    <div className="mt-4 px-4">
                        <ObjectListTableResizable
                            columns={dynamicColumns as any}
                            paginated_response={paginated_response}
                            allowAutoRedirect={false}
                            allowLateralScroll={false}
                            allowBatchSelection={false}
                            allowColumnResizing={false}
                            allowColumnOrdering={false}
                            layout="dense"
                            itemToId={item => item.id?.toString() || ""}
                            onRowNavigateTo={
                                onRowNavigateTo ||
                                ((item: any) => {
                                    handleRowClick(item);
                                    return null;
                                })
                            }
                            primary_column={clickable_field}
                            clickable_field={clickable_field}
                            expandedRowId={expandedRowId}
                            renderExpandedRow={
                                !onRowNavigateTo
                                    ? (item: any) => (
                                          <div className="animate-in fade-in-0 slide-in-from-top-1">
                                              <ExpandedRowDetail item={item} fields_to_display={fields_to_display} />
                                          </div>
                                      )
                                    : undefined
                            }
                        />
                        {count > results.length && (
                            <div className="mt-2 flex items-center justify-center gap-2">
                                <button className="rounded bg-gray-100 px-4 py-1 text-sm text-primary hover:bg-gray-200 disabled:opacity-50" onClick={loadMore} disabled={loading}>
                                    See more
                                </button>
                                {objectName && (
                                    <>
                                        <span>or</span>
                                        <Link
                                            className="rounded bg-gray-100 px-4 py-1 text-sm text-primary hover:bg-gray-200 disabled:opacity-50"
                                            to={`/${objectName}/related/${objectId}/${relatedCode}/`}
                                        >
                                            See all
                                        </Link>
                                    </>
                                )}
                                {loading && <LucideIcons.Loader className="h-4 w-4 animate-spin text-gray-500" />}
                            </div>
                        )}
                    </div>
                )}
                {loading && results.length === 0 && (
                    <div className="flex items-center justify-center py-8">
                        <LucideIcons.Loader className="h-6 w-6 animate-spin text-gray-500" />
                        <span className="ml-2 text-gray-500">Loading...</span>
                    </div>
                )}
            </div>

            {form && (
                <>
                    <DynamicObjectForm
                        open={createOpen}
                        onOpenChange={setCreateOpen}
                        mode="create"
                        formConfig={form.config}
                        endpointUrl={formEndpointUrl}
                        formIndex={form.form_index}
                        formScope="list"
                        fixedValues={{ [form.parent_field]: objectId }}
                        onSuccess={onMutated}
                    />
                    <DynamicObjectForm
                        open={editItem !== null}
                        onOpenChange={open => !open && setEditItem(null)}
                        mode="update"
                        formConfig={form.config}
                        endpointUrl={formEndpointUrl}
                        recordId={editItem?.id}
                        initialValues={editItem?.form_values}
                        formIndex={form.form_index}
                        formScope="list"
                        onSuccess={() => {
                            setEditItem(null);
                            onMutated();
                        }}
                    />
                    <AlertDialog open={deleteItem !== null} onOpenChange={open => !open && setDeleteItem(null)}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                                <AlertDialogDescription>Cette action est définitive et ne peut pas être annulée.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction variant="destructive" disabled={deleting} onClick={handleDelete}>
                                    Supprimer
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </>
            )}
        </div>
    );
}
